import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Building2, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { PageStatCards } from "@/components/PageStatCards";
import { TableToolbar } from "@/components/TableToolbar";
import { RowActions } from "@/components/RowActions";
import { DetailDrawer, DrawerSection, DrawerField } from "@/components/DetailDrawer";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";
import CompaniesDesign from "./CompaniesDesign";
import { CompanyDetailsForm } from "@/components/CompanyDetailsForm";

const EMPTY = { code: "", name: "", gstin: "", state: "", address: "" };

export default function Companies() {
  const { isOrgAdmin } = useAuth();
  const { refresh: refreshCompanyCtx, singleCompany } = useCompany() || {};
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/masters/companies");
      setCompanies(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (company) => {
    try {
      await api.patch(`/masters/companies/${company.id}`, { is_active: !company.is_active });
      load();
      refreshCompanyCtx?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const shown = useMemo(() => {
    if (!search) return companies;
    const q = search.toLowerCase();
    return companies.filter((c) =>
      (c.code || "").toLowerCase().includes(q) ||
      (c.name || "").toLowerCase().includes(q) ||
      (c.gstin || "").toLowerCase().includes(q) ||
      (c.state || "").toLowerCase().includes(q)
    );
  }, [companies, search]);

  return (
    <div className="space-y-6" data-testid="companies-page">
      <CompaniesDesign companies={companies} shown={shown} loading={loading} search={search} onSearch={setSearch} isOrgAdmin={isOrgAdmin}
        onToggle={toggleActive} onView={setSelected}
        createAction={isOrgAdmin && !singleCompany && <CreateCompanyDialog onCreated={() => { load(); refreshCompanyCtx?.(); }} />}
        renderDelete={(c) => isOrgAdmin && <DeleteCompanyDialog company={c} onDeleted={() => { load(); refreshCompanyCtx?.(); }} onDeactivate={() => toggleActive(c)} />} />

      <DetailDrawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.name}
        subtitle={`Company · ${selected?.code}`}
      >
        <DrawerSection label="Company Details">
          <DrawerField label="Code" value={selected?.code} mono />
          <DrawerField label="Name" value={selected?.name} />
          <DrawerField label="GSTIN" value={selected?.gstin} mono />
          <DrawerField label="PAN" value={selected?.pan} mono />
          <DrawerField label="State" value={selected?.state} />
          <DrawerField label="Status" value={selected?.is_active ? "Active" : "Inactive"} />
        </DrawerSection>
        <DrawerSection label="Contact">
          <DrawerField label="Phone" value={selected?.phone} />
          <DrawerField label="Email" value={selected?.email} />
          <DrawerField label="Website" value={selected?.website} />
        </DrawerSection>
        {selected?.address && (
          <DrawerSection label="Address">
            <p className="text-xs text-muted-foreground">{selected.address}</p>
          </DrawerSection>
        )}
        {isOrgAdmin && selected && (
          <Button className="mt-4 w-full" onClick={() => setEditing(true)} data-testid="edit-company-details-button">Edit details</Button>
        )}
      </DetailDrawer>
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Edit company details</DialogTitle></DialogHeader>
          {selected && <CompanyDetailsForm company={selected} onSaved={(c) => { setSelected(c); setEditing(false); load(); }} onLogoChanged={(c) => { setSelected(c); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateCompanyDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      await api.post("/masters/companies", form);
      toast.success(`Company ${form.code} created`);
      setForm(EMPTY);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-company-button" className="h-11 gap-2 rounded-lg bg-[#0F7C8C] px-5 text-sm font-semibold text-white shadow-sm hover:bg-[#0B6573]"><Plus className="h-4 w-4" /> New Company</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Company</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-company-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Code</Label>
              <Input placeholder="Automatic if blank" data-testid="company-code-input" value={form.code} onChange={set("code")} className="mt-1.5" />
              <FieldError error={errors.code} />
            </div>
            <div>
              <Label>Name</Label>
              <Input required data-testid="company-name-input" value={form.name} onChange={set("name")} className="mt-1.5" placeholder="SA Construction" />
              <FieldError error={errors.name} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>GSTIN</Label>
              <Input data-testid="company-gstin-input" value={form.gstin} onChange={set("gstin")} className="mt-1.5" />
            </div>
            <div>
              <Label>State</Label>
              <Input data-testid="company-state-input" value={form.state} onChange={set("state")} className="mt-1.5" placeholder="Rajasthan" />
            </div>
          </div>
          <div>
            <Label>Address</Label>
            <Textarea data-testid="company-address-input" value={form.address} onChange={set("address")} className="mt-1.5" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-company-submit-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteCompanyDialog({ company, onDeleted, onDeactivate }) {
  const [open, setOpen] = useState(false);
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);

  const openDialog = async () => {
    setOpen(true);
    setUsage(null);
    try {
      const { data } = await api.get(`/masters/companies/${company.id}/usage`);
      setUsage(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
      setOpen(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/masters/companies/${company.id}`);
      toast.success(`${company.name} deleted`);
      setOpen(false);
      onDeleted?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="sm" variant="ghost" title="Delete company" className="h-9 w-9 rounded-lg bg-[#FDE8EC] p-0 hover:bg-[#FAD5DC] dark:bg-[#4E2634]" onClick={openDialog} data-testid={`delete-company-${company.code}`}>
        <Trash2 className="h-4 w-4 text-rose-500" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Delete {company.name}?</DialogTitle></DialogHeader>
          {!usage ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Checking whether this company has any data…</p>
          ) : usage.last_company ? (
            <p className="text-sm text-muted-foreground">This is your only company. Create another company before deleting this one.</p>
          ) : !usage.can_delete ? (
            <div className="space-y-3 text-sm">
              <p className="text-rose-600 dark:text-rose-400">This company can't be deleted because it already has data:</p>
              <ul className="list-disc space-y-0.5 pl-5 text-muted-foreground">
                {usage.blocking.map((r) => <li key={r.table}>{r.count} {r.label}</li>)}
              </ul>
              <p className="text-muted-foreground">Deleting it would destroy that history. Switch it to <b>Inactive</b> instead — it disappears from selections but its records stay for reports and audit.</p>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <p className="text-foreground">No accounts, projects or documents were ever recorded in this company, so it can be deleted.</p>
              {usage.setup_to_remove.length > 0 && (
                <p className="text-muted-foreground">Its set-up records will be removed too: {usage.setup_to_remove.map((r) => `${r.count} ${r.label}`).join(", ")}.</p>
              )}
              <p className="text-xs text-muted-foreground">This cannot be undone.</p>
            </div>
          )}
          <DialogFooter>
            {usage && !usage.can_delete && !usage.last_company && company.is_active && (
              <Button variant="outline" onClick={() => { setOpen(false); onDeactivate?.(); }}>Make inactive</Button>
            )}
            {usage?.can_delete && (
              <Button variant="destructive" disabled={busy} onClick={remove} data-testid="confirm-delete-company">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Delete company
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
